import { useState, useEffect } from 'react';
import { subscribeToNotifications, markNotificationAsRead, deleteNotification } from '../services/notificationService';
import { acceptTripInvite, rejectTripInvite } from '../services/tripService';
import { useNavigate } from 'react-router-dom';

function NotificationBanner({ userId }) {
  const [notifications, setNotifications] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const [processingInvite, setProcessingInvite] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) return;

    // Subscribe to real-time notifications
    const unsubscribe = subscribeToNotifications(userId, (newNotifications) => {
      // Only show unread notifications that haven't been dismissed
      const unread = newNotifications.filter(n => !n.read && !dismissedIds.has(n.id));
      setNotifications(unread);
    });

    return () => unsubscribe();
  }, [userId, dismissedIds]);

  const handleDismiss = async (notification) => {
    // Add to dismissed set immediately for instant UI feedback
    setDismissedIds(prev => new Set([...prev, notification.id]));

    // Mark as read in Firestore
    await markNotificationAsRead(notification.id);
  };

  const handleDelete = async (notification) => {
    // Add to dismissed set immediately
    setDismissedIds(prev => new Set([...prev, notification.id]));

    // Delete from Firestore
    await deleteNotification(notification.id);
  };

  const handleAcceptInvite = async (notification) => {
    if (!notification.inviteCode) return;

    setProcessingInvite(notification.id);

    try {
      const result = await acceptTripInvite(notification.inviteCode, userId);

      if (result.success) {
        // Mark notification as read and dismiss
        await handleDelete(notification);

        // Navigate to the trip
        navigate(`/trips/${notification.tripId}`);
      } else {
        console.error('Failed to accept invite:', result.error);
        alert('Failed to accept invitation: ' + result.error);
        setProcessingInvite(null);
      }
    } catch (error) {
      console.error('Error accepting invite:', error);
      alert('An error occurred while accepting the invitation');
      setProcessingInvite(null);
    }
  };

  const handleRejectInvite = async (notification) => {
    if (!notification.inviteCode) return;

    setProcessingInvite(notification.id);

    try {
      const result = await rejectTripInvite(notification.inviteCode, userId);

      if (result.success) {
        // Delete the notification
        await handleDelete(notification);
      } else {
        console.error('Failed to reject invite:', result.error);
        alert('Failed to reject invitation: ' + result.error);
        setProcessingInvite(null);
      }
    } catch (error) {
      console.error('Error rejecting invite:', error);
      alert('An error occurred while rejecting the invitation');
      setProcessingInvite(null);
    }
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-md">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className={`bg-white rounded-lg shadow-lg border-l-4 p-4 animate-slide-in ${
            notification.type === 'image_moderation'
              ? 'border-red-500'
              : notification.type === 'trip_invite'
              ? 'border-purple-500'
              : 'border-blue-500'
          }`}
          role="alert"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1">
              {/* Icon */}
              <div className={`mt-0.5 ${
                notification.type === 'image_moderation'
                  ? 'text-red-500'
                  : notification.type === 'trip_invite'
                  ? 'text-purple-500'
                  : 'text-blue-500'
              }`}>
                {notification.type === 'image_moderation' ? (
                  <i className="fas fa-exclamation-triangle text-xl"></i>
                ) : notification.type === 'trip_invite' ? (
                  <i className="fas fa-envelope-open text-xl"></i>
                ) : (
                  <i className="fas fa-info-circle text-xl"></i>
                )}
              </div>

              {/* Content */}
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-1">
                  {notification.title}
                </h4>
                <p className="text-sm text-gray-700">
                  {notification.message}
                </p>
                {notification.type === 'trip_invite' && notification.accessLevel && (
                  <div className="mt-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      notification.accessLevel === 'edit'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {notification.accessLevel === 'edit' ? 'Can Edit' : 'View Only'}
                    </span>
                  </div>
                )}
                {notification.tripId && notification.type !== 'trip_invite' && (
                  <a
                    href={`/trips/${notification.tripId}`}
                    className="text-sm text-blue-600 hover:text-blue-700 mt-2 inline-block"
                  >
                    View Trip →
                  </a>
                )}
              </div>
            </div>

            {/* Close button */}
            <button
              onClick={() => handleDismiss(notification)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Dismiss notification"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* Action buttons */}
          <div className="mt-3 flex gap-2">
            {notification.type === 'trip_invite' ? (
              <>
                <button
                  onClick={() => handleAcceptInvite(notification)}
                  disabled={processingInvite === notification.id}
                  className="text-sm px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {processingInvite === notification.id ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-1"></i>
                      Accepting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check mr-1"></i>
                      Accept
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleRejectInvite(notification)}
                  disabled={processingInvite === notification.id}
                  className="text-sm px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processingInvite === notification.id ? (
                    'Processing...'
                  ) : (
                    <>
                      <i className="fas fa-times mr-1"></i>
                      Reject
                    </>
                  )}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => handleDismiss(notification)}
                  className="text-sm px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
                >
                  Dismiss
                </button>
                <button
                  onClick={() => handleDelete(notification)}
                  className="text-sm px-3 py-1 text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default NotificationBanner;
